import React from 'react'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { MultiScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { LinkButton } from '../../../components/Button/LinkButton'
import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import './Settings.scss'

export const MeetingDeletionSettings: React.FC = () => {
  useDocumentTitle('Setup')
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [updateAccount] = useUpdateAccountMutation()
  const { hasQuota } = useZocksQuota()
  const restrictedSessionDeleteSettingAvailable = hasQuota('room.restrictedSessionDelete.value')

  const restrictedSessionDelete = account?.data.effectiveConfig.restrictedSessionDelete ?? false
  const restrictedSessionDeleteEnterpriseControlled = account?.data.enterpriseConfig.restrictedSessionDelete != null

  const handleRestrictedSessionDelete = (checked: boolean) => {
    if (!account) {
      return
    }
    updateAccount({
      accountId: accountId,
      payload: {
        config: { ...account.data.config, restrictedSessionDelete: checked },
      },
    })
  }

  return (
    <>
      <Container className='d-flex flex-column align-items-start zocks-gap-2'>
        <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <Stack direction='horizontal' gap={3}>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  By default anyone can delete the meetings they created, you can restrict meeting deletion to the Owner and Admin role.
                </span>
              </div>
              {!restrictedSessionDeleteSettingAvailable && (
                <MultiScopedBlock
                  scopedBlocks={[
                    {
                      scope: { scope: 'account:compliance_config_update', superScope: [] },
                      missingScopeAction: MissingScopeAction.DISABLE,
                    },
                    {
                      scope: { scope: 'account:update', superScope: ['account:admin'] },
                      missingScopeAction: MissingScopeAction.DISABLE,
                    },
                  ]}
                >
                  <LinkButton to='/account/plan' variant='primary' className='d-flex p-1 p-2 align-items-center text-nowrap'>
                    <img alt='Upgrade' src={PrizeBadge} />
                    Get feature
                  </LinkButton>
                </MultiScopedBlock>
              )}
            </Stack>
          </Col>
        </Row>
        {restrictedSessionDeleteEnterpriseControlled ? (
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>The restriction on meeting session deletion is managed at the enterprise level.</span>
              </div>
            </Col>
          </Row>
        ) : null}
        <Row className='gap-3 p-0 align-items-center'>
          <Col xs='auto' className='p-0'>
            <div className='paragraph-medium'>Disallow users with User role to delete meetings</div>
          </Col>
          <Col className='p-0'>
            <MultiScopedBlock
              scopedBlocks={[
                {
                  scope: { scope: 'account:compliance_config_update', superScope: [] },
                  missingScopeAction: MissingScopeAction.DISABLE,
                },
                {
                  scope: { scope: 'account:update', superScope: ['account:admin'] },
                  missingScopeAction: MissingScopeAction.DISABLE,
                },
              ]}
            >
              <Form.Switch
                disabled={isAccountFetching || !restrictedSessionDeleteSettingAvailable || restrictedSessionDeleteEnterpriseControlled}
                checked={restrictedSessionDelete}
                onClick={(e) => handleRestrictedSessionDelete(e.currentTarget.checked)}
              />
            </MultiScopedBlock>
          </Col>
        </Row>
      </Container>
    </>
  )
}
